/**
 * fareHistoryChartView — the geometry of FareHistoryChart, pure so it can be tested.
 * One line per fare type, labelled at its end. A null price breaks the line: nothing is
 * drawn across a check where the fare was not on sale.
 */
import type { FareHistorySeries } from "@/lib/types";

export const CHART_WIDTH = 343;
const LEFT = 38;
const RIGHT = 116;
const TOP = 10;
const BOTTOM = 22;
const LABEL_H = 27;
const MIN_PLOT_H = 130;
/** Beyond this many checks the line alone reads better than a dot on each. */
const DOTS_UP_TO = 14;
/** The palette has eight validated slots. A ninth fare is never given a made-up colour. */
export const MAX_LINES = 8;

export interface ChartPoint {
  x: number;
  y: number;
  at: string;
  price: number;
  /** Drawn even with many checks: a lone point between gaps has no line to show it. */
  alone: boolean;
}

export interface ChartLine {
  key: string;
  level: string;
  /** Unbroken runs of points. More than one run means a gap. */
  runs: ChartPoint[][];
  points: ChartPoint[];
  showDots: boolean;
  label: { y: number; lineY: number; name: string; price: string };
}

export interface ChartLayout {
  width: number;
  height: number;
  plot: { left: number; right: number; top: number; bottom: number };
  yTicks: { y: number; label: string }[];
  xLabels: { x: number; label: string; anchor: "start" | "end" }[];
  lines: ChartLine[];
  hasGap: boolean;
  foldedOut: number;
}

export const money = (n: number): string => "$" + Math.round(n).toLocaleString("en-US");

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Nov 2", in UTC: the same day the summary sentence counts. */
export function shortDay(at: string): string {
  const d = new Date(at);
  if (isNaN(d.getTime())) return "";
  return MONTHS[d.getUTCMonth()] + " " + d.getUTCDate();
}

export function shortName(level: string): string {
  return level.length <= 18 ? level : level.slice(0, 17).trimEnd() + "…";
}

/** Move labels apart so none overlap, staying inside the plot height. */
function spreadLabels(ys: number[], top: number, bottom: number): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  for (let k = 1; k < order.length; k++) {
    if (order[k].y - order[k - 1].y < LABEL_H) order[k].y = order[k - 1].y + LABEL_H;
  }
  const over = order.length ? order[order.length - 1].y - bottom : 0;
  if (over > 0) {
    order[order.length - 1].y -= over;
    for (let k = order.length - 2; k >= 0; k--) {
      if (order[k + 1].y - order[k].y < LABEL_H) order[k].y = order[k + 1].y - LABEL_H;
    }
  }
  // Lifting the stack can push its head above the top: walk down again from there, so
  // two labels never share a line even if the last one runs a little past the bottom.
  if (order.length) order[0].y = Math.max(order[0].y, top);
  for (let k = 1; k < order.length; k++) {
    if (order[k].y - order[k - 1].y < LABEL_H) order[k].y = order[k - 1].y + LABEL_H;
  }
  const out: number[] = [];
  for (const o of order) out[o.i] = o.y;
  return out;
}

export function chartLayout(allSeries: FareHistorySeries[]): ChartLayout {
  const series = allSeries.slice(0, MAX_LINES);
  const plotH = Math.max(MIN_PLOT_H, series.length * LABEL_H + 8);
  const height = TOP + plotH + BOTTOM;
  const plot = { left: LEFT, right: CHART_WIDTH - RIGHT, top: TOP, bottom: TOP + plotH };

  const sold = series.flatMap((s) => s.points.filter((p) => p.price != null).map((p) => p.price as number));
  const times = series.flatMap((s) => s.points.map((p) => new Date(p.at).getTime()));
  const lo = sold.length ? Math.min(...sold) : 0;
  const hi = sold.length ? Math.max(...sold) : 1;
  const pad = hi === lo ? Math.max(10, hi * 0.1) : 0;
  const yLo = lo - pad;
  const yHi = hi + pad;
  const t0 = times.length ? Math.min(...times) : 0;
  const t1 = times.length ? Math.max(...times) : 1;

  const xOf = (at: string) =>
    t1 === t0 ? (plot.left + plot.right) / 2 : plot.left + ((new Date(at).getTime() - t0) / (t1 - t0)) * (plot.right - plot.left);
  const yOf = (price: number) => plot.bottom - ((price - yLo) / (yHi - yLo)) * plotH;

  let hasGap = false;
  const built = series.map((s) => {
    const runs: ChartPoint[][] = [];
    let cur: ChartPoint[] = [];
    for (const p of s.points) {
      if (p.price == null) {
        hasGap = true;
        if (cur.length) runs.push(cur);
        cur = [];
      } else {
        cur.push({ x: xOf(p.at), y: yOf(p.price), at: p.at, price: p.price, alone: false });
      }
    }
    if (cur.length) runs.push(cur);
    for (const r of runs) if (r.length === 1) r[0].alone = true;
    return { s, runs, points: runs.flat() };
  });
  const labelYs = spreadLabels(
    built.map((b) => (b.points.length ? b.points[b.points.length - 1].y : plot.top)),
    plot.top + 8,
    plot.bottom - 8,
  );

  const lines: ChartLine[] = built.map((b, i) => ({
    key: b.s.cabin + "/" + b.s.level,
    level: b.s.level,
    runs: b.runs,
    points: b.points,
    showDots: b.points.length <= DOTS_UP_TO,
    label: {
      y: labelYs[i],
      lineY: b.points.length ? b.points[b.points.length - 1].y : labelYs[i],
      name: shortName(b.s.level),
      price: b.s.lastPrice == null ? "" : money(b.s.lastPrice) + (b.s.soldAtLastCheck ? "" : " last seen"),
    },
  }));

  const firstAt = times.length ? new Date(t0).toISOString() : null;
  const lastAt = times.length ? new Date(t1).toISOString() : null;
  return {
    width: CHART_WIDTH,
    height,
    plot,
    yTicks: sold.length
      ? hi === lo
        ? [{ y: yOf(hi), label: money(hi) }]
        : [
            { y: yOf(hi), label: money(hi) },
            { y: yOf(lo), label: money(lo) },
          ]
      : [],
    xLabels:
      firstAt && lastAt
        ? shortDay(firstAt) === shortDay(lastAt)
          ? [{ x: plot.left, label: shortDay(firstAt), anchor: "start" }]
          : [
              { x: plot.left, label: shortDay(firstAt), anchor: "start" },
              { x: plot.right, label: shortDay(lastAt), anchor: "end" },
            ]
        : [],
    lines,
    hasGap,
    foldedOut: allSeries.length - series.length,
  };
}
